import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { updateUserSettings, uploadProfileImage, deleteProfileImage } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { 
  User, Mail, Building, Shield, Bell, Moon, Sun, Palette, Camera, Trash2, Hash
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SettingsPage = () => {
  const { user, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [notificationEnabled, setNotificationEnabled] = useState(user?.notification_enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const getProfileImageUrl = () => {
    if (user?.profile_image) {
      return `${API_URL}/api/users/${user.id}/profile-image`;
    }
    return null;
  };

  const handleNotificationToggle = async (enabled) => {
    setNotificationEnabled(enabled);
    setSaving(true);
    try {
      await updateUserSettings(user.id, { notification_enabled: enabled });
      updateUser({ notification_enabled: enabled });
      toast.success(`Notifications ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setNotificationEnabled(!enabled);
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleThemeToggle = async () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    toggleTheme();
    try {
      await updateUserSettings(user.id, { theme_preference: newTheme });
      updateUser({ theme_preference: newTheme });
    } catch (error) {
      console.error('Failed to save theme preference');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 2MB');
      return;
    }

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are supported');
      return;
    }

    setUploadingImage(true);
    try {
      await uploadProfileImage(user.id, file);
      updateUser({ profile_image: true });
      toast.success('Profile image updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteImage = async () => {
    try {
      await deleteProfileImage(user.id);
      updateUser({ profile_image: null });
      toast.success('Profile image removed');
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Super Admin': return 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/20';
      case 'Admin': return 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20';
      case 'Coordinator': return 'bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/20';
      default: return 'bg-gray-500/15 text-gray-600 dark:text-gray-400 border border-gray-500/20';
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-heading font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">Manage your account preferences</p>
      </div>

      {/* Profile Card */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Profile Image Section */}
          <div className="flex items-center gap-5">
            <div className="relative group">
              <Avatar className="h-20 w-20 bg-[#F5A623]/20" data-testid="profile-avatar">
                <AvatarImage src={getProfileImageUrl()} key={user?.profile_image ? 'img' : 'no-img'} />
                <AvatarFallback className="text-[#F5A623] font-semibold text-2xl">
                  {user?.full_name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="absolute inset-0 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                disabled={uploadingImage}
                data-testid="upload-image-btn"
              >
                <Camera className="h-5 w-5 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageUpload}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{user?.full_name}</h3>
              <Badge className={getRoleColor(user?.role)}>{user?.role}</Badge>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  data-testid="change-photo-btn"
                >
                  <Camera className="h-3.5 w-3.5 mr-1.5" />
                  {uploadingImage ? 'Uploading...' : 'Change Photo'}
                </Button>
                {user?.profile_image && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDeleteImage}
                    className="text-destructive hover:text-destructive"
                    data-testid="remove-photo-btn"
                  >
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid gap-4">
            <div className="flex items-center gap-3">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Employee ID</p>
                <p className="font-medium font-mono" data-testid="employee-id">{user?.emp_code || '-'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Building className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Department</p>
                <p className="font-medium">{user?.department}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Role</p>
                <p className="font-medium">{user?.role}</p>
              </div>
            </div>
            
            {user?.manager_name && (
              <div className="flex items-center gap-3">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Reports To</p>
                  <p className="font-medium">{user?.manager_name}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Preferences Card */}
      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            Preferences
          </CardTitle>
          <CardDescription>Customize your experience</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {theme === 'dark' ? (
                <Moon className="h-5 w-5 text-muted-foreground" />
              ) : (
                <Sun className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <Label className="text-base">Theme</Label>
                <p className="text-sm text-muted-foreground">
                  Currently using {theme} mode
                </p>
              </div>
            </div>
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={handleThemeToggle}
              data-testid="theme-toggle"
            />
          </div>

          <Separator />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-5 w-5 text-muted-foreground" />
              <div>
                <Label className="text-base">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive email notifications for task updates
                </p>
              </div>
            </div>
            <Switch
              checked={notificationEnabled}
              onCheckedChange={handleNotificationToggle}
              disabled={saving}
              data-testid="notification-toggle"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
